import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { spacePath } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { ElementKind } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Card, EmptyState, Loading } from "../../../ui";
import { ElementSearchPicker } from "../components/ElementPicker";
import { ShapeEditor } from "../components/symbols/ShapeEditor";
import { SymbolBrowser } from "../components/symbols/SymbolBrowser";
import { PickFor } from "../components/symbols/SymbolPick";
import { SymbolUpload } from "../components/symbols/SymbolUpload";
import { useDocumentKinds } from "../hooks";

/** Everything but items and component kinds, left out when the picker looks for the element a symbol is for. */
const NOT_DRAWN_KINDS: ElementKind[] = ["family", "sub_family", "library", "component", "capability", "data_field", "data_group", "pick_list", "document_kind", "document_link", "link", "spec_chapter", "framework"];

/**
 * The screen at /symbols (the symbols change of 2026-10-31): the symbol library that ships with the framework, with
 * this discipline's own shapes beside it, drawn from their data; one shape at a time with its connection points, its
 * marks, its registration and its withdrawal; the upload of a drawing; and one pick for each kind B drawing an item or
 * component kind is shown on, with the roles of the connection points.
 */
export function SymbolsPage() {
  const [params, setParams] = useSearchParams();
  const elementId = params.get("element") ?? "";
  const shapeCode = params.get("shape") ?? "";
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const symbols = useApiQuery("symbol_set", {});
  const kinds = useDocumentKinds();
  const chosenShape = symbols.data?.items.find((item) => item.code === shapeCode) ?? null;
  const shapeCard = useRef<HTMLDivElement>(null);
  const wantFocus = useRef(false);
  const [notice, setNotice] = useState<{ code: string; warnings: string[] } | null>(null);
  const drawingName = (code: string) => kinds.kinds.find((k) => k.code === code)?.name ?? code;

  const setParam = (name: string, value: string) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(name, value);
        else next.delete(name);
        return next;
      },
      { replace: true },
    );

  const openShape = (code: string) => {
    wantFocus.current = !!code;
    setParam("shape", code);
  };

  // The opened shape sits below the whole library: bring it into view and put focus on its heading, so that a click
  // on a tile near the bottom visibly opens it and a keyboard user lands on it.
  useEffect(() => {
    if (!chosenShape || !wantFocus.current) return;
    wantFocus.current = false;
    shapeCard.current?.scrollIntoView?.({ block: "start" });
    document.getElementById("shape-title")?.focus({ preventScroll: true });
  }, [chosenShape]);

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="symbols-title">
        <h1 id="symbols-title">Symbol library</h1>
        <p>
          The library holds the common symbols and line styles; this discipline&apos;s own shapes sit beside them. A symbol is a drawing in
          millimetres with its connection points, where lines attach; there is no picture file. Each item or component kind gets one symbol for each drawing that shows symbols it is
          shown on, and a drawn Connection gets a line style. A geometric drawing that a team adds, such as a plot plan, needs none.
        </p>
        <p className="muted">
          For a hand-over, <a href={spacePath("symbol_library", "/api/ws/symbols/library")} download="symbol-library.yaml">download the whole library as one file</a>.
        </p>
      </Card>

      <Card aria-labelledby="stored-title">
        <h2 id="stored-title">Browse the library</h2>
        {symbols.isPending ? (
          <Loading>Reading the symbol library</Loading>
        ) : symbols.isError ? (
          <EmptyState title="The symbol library could not be read">{refusalText(symbols.error)}</EmptyState>
        ) : symbols.data.items.length === 0 ? (
          <EmptyState title="The library is empty">No shape comes with this installation yet. Upload the first drawing below.</EmptyState>
        ) : (
          <SymbolBrowser set={symbols.data} selected={shapeCode || null} drawingName={drawingName} onSelect={(code) => openShape(code === shapeCode ? "" : code)} />
        )}
      </Card>

      {chosenShape && (
        <div ref={shapeCard}>
          <Card aria-label={`The shape ${chosenShape.code}`}>
            {notice && notice.code === chosenShape.code && notice.warnings.length > 0 && (
              <div className="shape-notice" role="status">
                <p>Stored. What to know about it:</p>
                <ul>
                  {notice.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            <ShapeEditor key={chosenShape.code} item={chosenShape} editable={editable} items={symbols.data?.items ?? []} drawingName={drawingName} />
          </Card>
        </div>
      )}

      {editable && (
        <SymbolUpload
          drawings={kinds.drawings.filter((k) => k.deliverableKind === "B")}
          loading={kinds.loading}
          onStored={(code, warnings) => {
            setNotice({ code, warnings });
            openShape(code);
          }}
        />
      )}

      <Card aria-labelledby="pick-title">
        <h2 id="pick-title">Pick for an item or a component kind</h2>
        <ElementSearchPicker label="Which one" value={elementId} exclude={NOT_DRAWN_KINDS} onChange={(id) => setParam("element", id)} hint="Search by name. Only items and component kinds are drawn." />
        {elementId && <PickFor key={elementId} elementId={elementId} symbols={symbols} editable={editable} />}
      </Card>
    </div>
  );
}
